/**
 * `usePickList` — the fail-closed loader for live reference data (WSRADM-0003;
 * handoff §8.2).
 *
 * The reusable foundation behind every external-sourced list/axis (dropdowns,
 * lookups, matrix axes). It deliberately carries NO contract knowledge — the
 * caller passes a `fetcher` that returns the COMPLETE list or throws the shared
 * {@link PickListError} on any failure.
 *
 * Fail-closed contract:
 *  - **Load LIVE on mount; NEVER cache.** Built on `useResource`, which runs the
 *    fetcher on mount and exposes a stable `retry` that re-fetches live. There is
 *    no client-side memo/store — every mount and every `retry` hits the wire.
 *  - **loading → ready, or loading → error + Retry — never a partial list.**
 *    `items` is `null` until a COMPLETE load succeeds; a thrown error lands in
 *    `error` with `status: 'error'` and `items` STAYS `null` — a failure can
 *    never present as an empty list.
 *  - A successful EMPTY list is a real `ready` state with `items: []` — distinct
 *    from a failure. Consumers render "no options" for the former and
 *    error + Retry for the latter.
 *
 * `<PickListLoader>` (in `@/components/PickListLoader`) renders this state
 * machine; most screens use that wrapper rather than switching on the hook by
 * hand.
 */
import { useCallback } from 'react';
import { useResource, type ResourceStatus } from './useResource';
import { isPickListError, type PickListError } from '@/api/picklists';

export type PickListStatus = ResourceStatus;

export interface PickList<T> {
  /**
   * The fully-loaded list, or `null` until the first COMPLETE load succeeds.
   * Never holds a partial list: on failure it stays `null` while `status` is
   * `'error'`. An empty `[]` here means a successful empty list, not a failure.
   */
  items: T[] | null;
  /** Coarse load state the UI switches on (`loading` | `ready` | `error`). */
  status: PickListStatus;
  /** The error from the most recent failed load, else `null`. */
  error: unknown;
  /**
   * True when the failure is the distinct fail-closed `PickListError` — the
   * loader uses this to choose the "couldn't load …" copy. Any other thrown
   * error still surfaces as `error`.
   */
  failClosed: boolean;
  /**
   * Re-fetch the list LIVE (no cache). Stable identity across renders, so it is
   * safe to wire straight to a Retry button.
   */
  retry: () => void;
}

/**
 * Load a live pick-list and track its fail-closed state. `deps` mirrors a
 * `useEffect` dependency array (pass the values the fetcher closes over); omit for
 * a once-on-mount load with manual `retry`.
 *
 * @param fetcher returns the COMPLETE list, or throws on any failure (a wrapper
 *   throws a `PickListError`). The hook never converts a throw into an empty list.
 */
export function usePickList<T>(
  fetcher: () => Promise<T[]>,
  deps: readonly unknown[] = [],
): PickList<T> {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stableFetcher = useCallback(fetcher, deps);
  const resource = useResource(stableFetcher, [stableFetcher]);

  return {
    // `useResource.data` is `null` until the first success — exactly the
    // "never a partial list" guarantee we need, surfaced as `items`.
    items: resource.data,
    status: resource.status,
    error: resource.error,
    failClosed: isPickListError(resource.error),
    retry: resource.refetch,
  };
}

export type { PickListError };
